import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ClockPicker.css';

/**
 * A time of day as 'HH:MM' (24-hour, what the API speaks), shown and edited
 * as three pieces - hour, minute, AM/PM - each on its own: click one and type,
 * or step it with the arrow keys (hours by 1, minutes by 15), and type "a" or
 * "p" anywhere to set the half of the day. Entering the field opens a panel
 * under it with a column per piece, so any one can be clicked without
 * touching the others.
 *
 * `fallback` fills an empty field the moment it's entered (an end time 30
 * minutes after the start, say). `anchor` (a start time) adds the length to
 * the panel and quick +15m / +30m / +1h / +2h buttons. `busy` (entries, or
 * { start, end } clocks) dots the hours already logged. `onClose` runs when the panel closes.
 */

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const LENGTHS = [15, 30, 60, 120];
const PANEL_WIDTH = 300;
const PANEL_HEIGHT = 330;

const toMinutes = (clock) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(clock || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const toClock = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const label12 = (minutes) => {
  const h = Math.floor(minutes / 60);
  return `${((h + 11) % 12) + 1}:${String(minutes % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

const lengthLabel = (minutes) => {
  const h = Math.floor(minutes / 60);
  const r = minutes % 60;
  return h ? (r ? `${h}h ${r}m` : `${h}h`) : `${r}m`;
};

export default function ClockPicker({
  value,
  onChange,
  fallback = null,
  anchor = null,
  busy = null,
  onClose = null,
  disabled = false,
  cleanable = true,
  size = 'md',
  'aria-label': ariaLabel = 'Time',
}) {
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const meridiemRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [part, setPart] = useState(null);
  const [pos, setPos] = useState(null);

  const current = toMinutes(value);
  // What the pieces start from while the field is still empty.
  const base = current ?? toMinutes(fallback) ?? 9 * 60;
  const hour24 = Math.floor(base / 60);
  const pm = hour24 >= 12;
  const anchorMinutes = toMinutes(anchor);

  const put = (change) => {
    let h = hour24;
    let m = base % 60;
    if (change.hour !== undefined) {
      // A first hour typed into an empty field guesses the working day: 7-11
      // in the morning, 12-6 in the afternoon.
      const afternoon = current === null && fallback === null ? change.hour === 12 || change.hour <= 6 : pm;
      h = (change.hour % 12) + (afternoon ? 12 : 0);
    }
    if (change.minute !== undefined) m = change.minute;
    if (change.pm !== undefined) h = (h % 12) + (change.pm ? 12 : 0);
    if (change.step !== undefined) {
      const v = (((h * 60 + m + change.step) % 1440) + 1440) % 1440;
      h = Math.floor(v / 60);
      m = v % 60;
    }
    if (change.at !== undefined) {
      h = Math.floor(change.at / 60);
      m = change.at % 60;
    }
    onChange(toClock(h * 60 + m));
  };

  const close = () => {
    if (!open) return;
    setOpen(false);
    setPart(null);
    if (onClose) onClose();
  };

  // The panel lives in a portal, so no card or modal can clip it. It sits
  // under the field, or over it when there is no room below.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const below = window.innerHeight - rect.bottom >= PANEL_HEIGHT + 12 || rect.top < PANEL_HEIGHT + 12;
      setPos({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)),
        top: below ? rect.bottom + 6 : undefined,
        bottom: below ? undefined : window.innerHeight - rect.top + 6,
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) close();
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const enter = (which) => (e) => {
    if (current === null && fallback) onChange(fallback);
    setPart(which);
    setOpen(true);
    if (e.target.select) e.target.select();
  };

  const letters = (e) => {
    const key = String(e.key).toLowerCase();
    if ((key === 'a' || key === 'p') && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      put({ pm: key === 'p' });
      return true;
    }
    if (e.key === 'Escape' || e.key === 'Enter') {
      if (open) e.preventDefault();
      close();
      return true;
    }
    return false;
  };

  const typeHour = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (!digits) {
      if (cleanable) onChange(null);
      return;
    }
    let n = Number(digits.slice(-2));
    if (!(n >= 1 && n <= 12)) n = Number(digits.slice(-1));
    if (!(n >= 1 && n <= 12)) return;
    put({ hour: n });
    // 2-9, or a second digit, can't grow any more: on to the minutes.
    if (n >= 2 || digits.length >= 2) minuteRef.current?.focus();
  };

  const typeMinute = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (!digits) {
      if (cleanable) onChange(null);
      return;
    }
    const n = Number(digits.slice(-2));
    if (n >= 0 && n <= 59) put({ minute: n });
  };

  const onBlur = (e) => {
    const next = e.relatedTarget;
    if (next && (wrapRef.current?.contains(next) || panelRef.current?.contains(next))) return;
    close();
  };

  // Panel buttons never take focus, so the field keeps it (and its keys).
  const hold = (fn) => (e) => {
    e.preventDefault();
    fn();
  };

  const hourBusy = (h24) => Array.isArray(busy) && busy.some((b) => {
    const s = toMinutes(b.startTime ?? b.start);
    const t = toMinutes(b.endTime ?? b.end);
    return s !== null && t !== null && s < (h24 + 1) * 60 && h24 * 60 < t;
  });

  const span = anchorMinutes !== null && current !== null ? current - anchorMinutes : null;

  const panel = open && pos ? createPortal(
    <div
      ref={panelRef}
      className="ts-clock-panel"
      role="dialog"
      aria-label={ariaLabel}
      style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: PANEL_WIDTH }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="ts-clock-panel__head">
        <span className="ts-clock-panel__preview">{label12(base)}</span>
        {span !== null ? (
          <span className={`ts-clock-panel__note${span <= 0 ? ' is-warn' : ''}`}>
            {span > 0 ? lengthLabel(span) : 'before the start'}
          </span>
        ) : null}
      </div>
      <div className="ts-clock-panel__cols">
        <div className="ts-clock-panel__col" role="listbox" aria-label="Hour">
          {HOURS.map((n) => {
            const h24 = (n % 12) + (pm ? 12 : 0);
            const on = current !== null && h24 === hour24;
            return (
              <button
                key={n}
                type="button"
                tabIndex={-1}
                role="option"
                aria-selected={on}
                className={`ts-clock-panel__opt${on ? ' is-on' : ''}`}
                onMouseDown={hold(() => { put({ hour: n }); setPart('hour'); })}
              >
                {n}
                <span className={`ts-clock-panel__dot${hourBusy(h24) ? ' is-busy' : ''}`} aria-hidden="true" />
              </button>
            );
          })}
        </div>
        <div className="ts-clock-panel__col" role="listbox" aria-label="Minute">
          {MINUTES.map((mm) => {
            const on = current !== null && base % 60 === mm;
            return (
              <button
                key={mm}
                type="button"
                tabIndex={-1}
                role="option"
                aria-selected={on}
                className={`ts-clock-panel__opt${on ? ' is-on' : ''}${mm % 15 === 0 ? ' is-quarter' : ''}`}
                onMouseDown={hold(() => { put({ minute: mm }); setPart('minute'); })}
              >
                :{String(mm).padStart(2, '0')}
              </button>
            );
          })}
        </div>
        <div className="ts-clock-panel__col ts-clock-panel__col--half" role="listbox" aria-label="AM or PM">
          {[['AM', false], ['PM', true]].map(([text, isPm]) => {
            const on = current !== null && isPm === pm;
            return (
              <button
                key={text}
                type="button"
                tabIndex={-1}
                role="option"
                aria-selected={on}
                className={`ts-clock-panel__opt${on ? ' is-on' : ''}`}
                onMouseDown={hold(() => { put({ pm: isPm }); setPart('meridiem'); })}
              >
                {text}
              </button>
            );
          })}
        </div>
      </div>
      {anchorMinutes !== null ? (
        <div className="ts-clock-panel__chips">
          {LENGTHS.filter((m) => anchorMinutes + m < 1440).map((m) => (
            <button
              key={m}
              type="button"
              tabIndex={-1}
              className={`ts-clock-panel__chip${span === m ? ' is-on' : ''}`}
              onMouseDown={hold(() => put({ at: anchorMinutes + m }))}
            >
              +{lengthLabel(m)}
            </button>
          ))}
        </div>
      ) : null}
    </div>,
    document.body,
  ) : null;

  const seg = (which) => `ts-clock__seg${open && part === which ? ' is-active' : ''}`;

  return (
    <div
      ref={wrapRef}
      className={`ts-clock${size === 'sm' ? ' ts-clock--sm' : ''}${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
      role="group"
      aria-label={ariaLabel}
      onBlur={onBlur}
      onMouseDown={(e) => {
        // A click on the field's padding goes to the hour.
        if (e.target === wrapRef.current && !disabled) {
          e.preventDefault();
          hourRef.current?.focus();
        }
      }}
    >
      <input
        ref={hourRef}
        className={`${seg('hour')} ts-clock__seg--hour`}
        type="text"
        inputMode="numeric"
        aria-label={`${ariaLabel}, hour`}
        placeholder="--"
        value={current === null ? '' : String(((hour24 + 11) % 12) + 1)}
        onFocus={enter('hour')}
        onChange={typeHour}
        onKeyDown={(e) => {
          if (letters(e)) return;
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            put({ step: e.key === 'ArrowUp' ? 60 : -60 });
          } else if (e.key === 'ArrowRight' || e.key === ':') {
            e.preventDefault();
            minuteRef.current?.focus();
          }
        }}
        disabled={disabled}
      />
      <span className="ts-clock__colon" aria-hidden="true">:</span>
      <input
        ref={minuteRef}
        className={seg('minute')}
        type="text"
        inputMode="numeric"
        aria-label={`${ariaLabel}, minutes`}
        placeholder="--"
        value={current === null ? '' : String(current % 60).padStart(2, '0')}
        onFocus={enter('minute')}
        onChange={typeMinute}
        onKeyDown={(e) => {
          if (letters(e)) return;
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            put({ step: e.key === 'ArrowUp' ? 15 : -15 });
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            meridiemRef.current?.focus();
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            hourRef.current?.focus();
          }
        }}
        disabled={disabled}
      />
      <button
        ref={meridiemRef}
        type="button"
        className={`ts-clock__ampm${open && part === 'meridiem' ? ' is-active' : ''}`}
        aria-label={`${ariaLabel}, ${pm ? 'PM' : 'AM'}. Switch AM or PM`}
        onFocus={enter('meridiem')}
        onClick={() => put({ pm: !pm })}
        onKeyDown={(e) => {
          if (letters(e)) return;
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            put({ pm: !pm });
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            minuteRef.current?.focus();
          }
        }}
        disabled={disabled}
      >
        {current === null ? '--' : pm ? 'PM' : 'AM'}
      </button>
      <i className="bi bi-clock ts-clock__icon" aria-hidden="true" />
      {panel}
    </div>
  );
}
